import React, { useEffect, useRef, useState } from "react";
import { catchError, map, of, tap, Subject, switchMap, takeUntil } from "rxjs";
import PropTypes from "prop-types";
import Article from "../Publications/models/Article";
import apiClient from "../../services/apiClient";
import Btn from "../atoms/Btn";
import ExpandableArticle from "../Publications/components/ExpandableArticle";
import PubmedEntry from "./PubmedEntry";

function PublicationScreen({ entry, onEntryUpdated, allowPublicationUpdate }) {
  const destroy$ = useRef(new Subject()).current;
  const [pubId, setPubId] = useState(entry?.pubmedId);
  const [isLoading, setIsLoading] = useState(null);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const doiReg = new RegExp(/^.*?(10\..*)$/);
  const errMsg = "Error fetching PUBMED info";
  const exMsg = "This article is too generic to be linked to specific variants";
  const doiMsg = "Error while fetching article from DOI";

  function handleError(msg, e, val) {
    setError(e?.response?.detail || msg);
    console.error(e);
    return of(val);
  }

  function isDoi(publicationId) {
    const match = publicationId.match(doiReg);
    return match && match[1];
  }

  function fetchPubmedIdFromDoi(doi) {
    return of(null).pipe(
      switchMap(() => apiClient.getDoiToPmid({ doi: encodeURIComponent(doi) })),
      map((x) => x.trim()),
      catchError((e) => handleError(doiMsg, e, null))
    );
  }

  function search() {
    of(null)
      .pipe(
        map(() => pubId),
        tap(() => setIsLoading(true)),
        switchMap((x) => (isDoi(x) ? fetchPubmedIdFromDoi(x) : of(x))),
        switchMap((x) => (x ? apiClient.getPublication(x) : of(x))),
        map((x) => x?.results || []),
        map((x) => (x ? new Article(x[0]) : null)),
        catchError((e) => handleError(errMsg, e, null)),
        tap(() => setIsLoading(false)),
        tap((x) => {
          if (x.isSourcesExcluded) {
            setError(exMsg);
          } else {
            setSelectedArticle(x);
          }
        }),
        takeUntil(destroy$)
      )
      .subscribe();
  }

  function handleOnChange(ev) {
    setPubId(ev.target.value);
    setError(null);
  }

  useEffect(() => {
    if (!selectedArticle) return;

    const origPmid = entry.pubmedId;

    entry.pubmedId = selectedArticle.pubmedId;

    if (origPmid !== selectedArticle?.pubmedId) {
      entry.coAuthor = false;
      entry.agreeContact = false;
      entry.isFunctional = false;
    }

    onEntryUpdated(entry.clone());
  }, [selectedArticle]);

  function updateIsFunc(val) {
    entry.isFunctional = val;
    onEntryUpdated(entry.clone());
  }

  function updateCoAuthor(val) {
    entry.coAuthor = val;
    entry.agreeContact = false;
    onEntryUpdated(entry.clone());
  }

  function updateAgreeContact(val) {
    entry.agreeContact = entry.coAuthor && val;
    onEntryUpdated(entry.clone());
  }

  useEffect(() => {
    if (entry.pubmedId) search();
    return () => destroy$.next();
  }, []);

  return (
    <>
      <div className="tw-font-bold text-larger-3">1. Publication</div>

      {allowPublicationUpdate ? (
        <>
          <div>
            Enter the PubMed id (PMID) or DOI for the publication you wish to
            link (PMID works much more reliably)
          </div>
          <div className="tw-flex tw-max-w-screen-sm">
            <input
              type="text"
              autoComplete="off"
              className="focus:tw-border-primary tw-px-1 tw-py-1 tw-border tw-border-blueGray-300 tw-outline-none tw-flex-grow"
              placeholder="Enter a PUBMED ID or DOI..."
              value={pubId}
              onChange={handleOnChange}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />

            <Btn
              onSubmit={() => search()}
              className="btn tw-w-32 btn-success tw-border-2"
              disabled={isLoading}
              id="pmid-submit"
            >
              Find...
            </Btn>
          </div>
        </>
      ) : null}

      {error ? (
        <div className="tw-bg-red-300 tw-text-center tw-font-bold tw-text-white tw-py-6">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="tw-text-center tw-py-6">Loading...</div>
      ) : null}

      {selectedArticle && !isLoading && !error ? (
        <>
          <div className="tw-p-2 tw-border-blueGray-300 tw-border tw-my-2">
            <ExpandableArticle article={selectedArticle} withExpander={true} />
          </div>

          <div>
            <div className="tw-pt-2">Click all that apply:</div>
            <label className="tw-m-0 tw-block">
              <input
                type="checkbox"
                className="tw-mx-2"
                checked={entry.isFunctional || false}
                onKeyDown={(e) => {
                  if (e.key === "Enter") updateIsFunc(!entry.isFunctional);
                }}
                onChange={(e) => updateIsFunc(e.target.checked)}
              />
              This publication is confirmed by a functional study
            </label>
            <label className="tw-m-0 tw-block">
              <input
                type="checkbox"
                className="tw-mx-2"
                checked={entry.coAuthor || false}
                onKeyDown={(e) => {
                  if (e.key === "Enter") updateCoAuthor(!entry.coAuthor);
                }}
                onChange={(e) => updateCoAuthor(e.target.checked)}
              />
              I am a (co-)author of this publication
            </label>
            {entry.coAuthor ? (
              <label className="tw-m-0 tw-block">
                <input
                  type="checkbox"
                  className="tw-mx-2"
                  checked={entry.agreeContact || false}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      updateAgreeContact(!entry.agreeContact);
                  }}
                  onChange={(e) => updateAgreeContact(e.target.checked)}
                />
                I agree to be contactable by other VarSome users (with contact
                form, we will not give yout email address to anyone)
              </label>
            ) : null}
          </div>
        </>
      ) : null}
    </>
  );
}

PublicationScreen.propTypes = {
  allowPublicationUpdate: PropTypes.bool,
  entry: PropTypes.instanceOf(PubmedEntry),
  onEntryUpdated: PropTypes.func.isRequired,
};

PublicationScreen.defaultProps = {
  allowPublicationUpdate: true,
  entry: null,
};

export default PublicationScreen;
